if (!customElements.get('wlsn-mobile-nav')) {
  class WLSNMobileNav extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        active: 'active',
        mobileMenuActive: 'mobile-menu-active',
        bodyOpen: 'wlsn-mnav-open',
        bodyWidgetsHidden: 'wlsn-mnav-widgets-hidden',
        menuToggle: 'js-wlsn-header-menu-toggle',
        cartDrawerTrigger: 'js-wlsn-header-cart-drawer-trigger',
        scroll: 'js-wlsn-mnav-scroll',
        accordion: 'js-wlsn-mnav-acc',
        accordionToggle: 'js-wlsn-mnav-acc-toggle'
      };

      this.desktopBreakpoint = 1025;
      this.savedScrollY = 0;

      this.handleToggleClick = this.handleToggleClick.bind(this);
      this.handleAccordionClick = this.handleAccordionClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleDismiss = this.handleDismiss.bind(this);
      this.handleLinkClick = this.handleLinkClick.bind(this);
      this.syncFromHeader = this.syncFromHeader.bind(this);
    }

    connectedCallback() {
      this.scrollPane = this.querySelector(`.${this.classes.scroll}`);
      this.headerHosts = Array.from(document.querySelectorAll('wlsn-header, wlsn-header-nav'));
      this.menuToggles = Array.from(document.querySelectorAll(`.${this.classes.menuToggle}`));
      this.cartDrawerTriggers = Array.from(document.querySelectorAll(`.${this.classes.cartDrawerTrigger}`));

      /*
       * The header owns the hamburger/close icon swap, so it stays the source of truth for
       * open state and this drawer mirrors it. Listening for the toggle's click instead would
       * race the header's own handler, which registers later and would undo any state set here.
       */
      if (this.headerHosts.length > 0) {
        this.headerObserver = new MutationObserver(this.syncFromHeader);
        this.headerHosts.forEach((host) => {
          this.headerObserver.observe(host, { attributes: true, attributeFilter: ['class'] });
        });
      } else {
        this.menuToggles.forEach((toggle) => toggle.addEventListener('click', this.handleToggleClick));
      }

      this.cartDrawerTriggers.forEach((trigger) => trigger.addEventListener('click', this.handleDismiss));
      this.addEventListener('click', this.handleAccordionClick);
      this.addEventListener('click', this.handleLinkClick);
      document.addEventListener('keydown', this.handleKeydown);
      window.addEventListener('resize', this.handleResize);
      /* The search panel takes over the screen, so the drawer steps aside rather than stacking. */
      document.addEventListener('wlsn:search:open', this.handleDismiss);
    }

    disconnectedCallback() {
      if (this.headerObserver) this.headerObserver.disconnect();
      this.menuToggles.forEach((toggle) => toggle.removeEventListener('click', this.handleToggleClick));
      this.cartDrawerTriggers.forEach((trigger) => trigger.removeEventListener('click', this.handleDismiss));
      this.removeEventListener('click', this.handleAccordionClick);
      this.removeEventListener('click', this.handleLinkClick);
      document.removeEventListener('keydown', this.handleKeydown);
      window.removeEventListener('resize', this.handleResize);
      document.removeEventListener('wlsn:search:open', this.handleDismiss);
      this.setOpen(false);
    }

    get isDesktop() {
      return window.innerWidth >= this.desktopBreakpoint;
    }

    get isOpen() {
      return this.classList.contains(this.classes.active);
    }

    get hidesFloatingWidgets() {
      return this.dataset.hideFloatingWidgets === 'true';
    }

    /*
     * Two headers exist per page and which one shows depends on the width, so the visible one
     * can't be cached — the same reason wlsn-header.js re-queries its promo bar on every measure.
     */
    getVisibleHeader() {
      return this.headerHosts.find((header) => header.getClientRects().length > 0) || null;
    }

    /*
     * The drawer is fixed, so it needs the live bottom edge of whatever sits above it. Reading
     * the header's own bottom covers the promo bar and any sticky offset in one measurement.
     */
    setOffsetTop() {
      const header = this.getVisibleHeader();
      const anchor = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
      this.style.setProperty('--wlsn-mnav-top', `${anchor}px`);
    }

    syncFromHeader() {
      const open = this.headerHosts.some((host) => host.classList.contains(this.classes.mobileMenuActive));
      if (open === this.isOpen) return;
      this.setOpen(open);
    }

    handleToggleClick() {
      if (this.isDesktop) return;
      this.setOpen(!this.isOpen);
    }

    setOpen(open) {
      if (open === true) this.savedScrollY = window.scrollY;

      document.body.classList.toggle(this.classes.bodyOpen, open);

      if (this.hidesFloatingWidgets === true) {
        document.body.classList.toggle(this.classes.bodyWidgetsHidden, open);
      }

      /* Measure after the body lock lands so the header sits at its final position. */
      if (open === true) this.setOffsetTop();

      this.classList.toggle(this.classes.active, open);
      this.menuToggles.forEach((toggle) => toggle.setAttribute('aria-expanded', String(open)));

      if (open === false) {
        /* Hand the open state back so the header restores its hamburger icon. */
        this.headerHosts.forEach((host) => host.classList.remove(this.classes.mobileMenuActive));
        window.scrollTo({ top: this.savedScrollY, behavior: 'instant' });
        return;
      }

      if (this.scrollPane) this.scrollPane.scrollTop = 0;
    }

    /* Accordions open in place; the drawer's own scroll position is left alone. */
    handleAccordionClick(event) {
      const toggle = event.target.closest(`.${this.classes.accordionToggle}`);
      if (!toggle) return;

      const accordion = toggle.closest(`.${this.classes.accordion}`);
      if (!accordion) return;

      event.preventDefault();

      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      const body = document.getElementById(toggle.getAttribute('aria-controls'));

      toggle.setAttribute('aria-expanded', String(!expanded));
      accordion.classList.toggle(this.classes.active, !expanded);
      if (body) body.hidden = expanded;
    }

    handleLinkClick(event) {
      const link = event.target.closest('a[href]');
      if (!link) return;
      this.setOpen(false);
    }

    handleKeydown(event) {
      if (event.key !== 'Escape') return;
      if (!this.isOpen) return;
      this.setOpen(false);
    }

    handleDismiss() {
      if (!this.isOpen) return;
      this.setOpen(false);
    }

    handleResize() {
      if (this.isDesktop && this.isOpen) {
        this.setOpen(false);
        return;
      }
      if (this.isOpen) this.setOffsetTop();
    }
  }

  customElements.define('wlsn-mobile-nav', WLSNMobileNav);
}
